import { create } from 'zustand';
import { Subscription, User } from '@/types';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  currentVetId: string | null;
  currentVetName: string | null;
  subscription: Subscription | null;
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
  clearAuth: () => void;
  setCurrentVet: (vetId: string, vetName: string) => void;
  clearCurrentVet: () => void;
  setSubscription: (subscription: Subscription | null) => void;
}

const _loadUser = (): User | null => {
  try {
    const s = localStorage.getItem('auth_user');
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

export const useAuthStore = create<AuthState>((set) => ({
  user: _loadUser(),
  accessToken: localStorage.getItem('access_token'),
  refreshToken: localStorage.getItem('refresh_token'),
  isAuthenticated: !!localStorage.getItem('access_token'),
  currentVetId: localStorage.getItem('current_vet_id'),
  currentVetName: localStorage.getItem('current_vet_name'),
  subscription: null,

  setAuth: (user, accessToken, refreshToken) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('auth_user', JSON.stringify(user));
    console.log('[DEBUG authStore] setAuth called, isInExtension=', window.parent !== window, 'token_preview=', accessToken.substring(0, 20) + '...');
    // Sync to extension storage so tokens survive panel close/reopen.
    // Only send when running inside an iframe (i.e. the extension side panel).
    if (window.parent !== window) {
      console.log('[DEBUG authStore] posting VETAI_STORE_TOKENS (access+refresh) to parent');
      window.parent.postMessage({
        type: 'VETAI_STORE_TOKENS',
        payload: { access_token: accessToken, refresh_token: refreshToken },
      }, '*');
    }
    set({
      user,
      accessToken,
      refreshToken,
      isAuthenticated: true,
    });
  },

  clearAuth: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('current_vet_id');
    localStorage.removeItem('current_vet_name');
    localStorage.removeItem('auth_user');
    // Clear from extension storage on logout
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'VETAI_CLEAR_TOKENS' }, '*');
    }
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      currentVetId: null,
      currentVetName: null,
    });
  },

  setCurrentVet: (vetId: string, vetName: string) => {
    localStorage.setItem('current_vet_id', vetId);
    localStorage.setItem('current_vet_name', vetName);
    console.log('[DEBUG authStore] setCurrentVet called:', vetId, vetName, 'isInExtension=', window.parent !== window);
    // Sync vet info to extension storage
    if (window.parent !== window) {
      console.log('[DEBUG authStore] posting VETAI_STORE_TOKENS (vet info) to parent');
      window.parent.postMessage({
        type: 'VETAI_STORE_TOKENS',
        payload: { current_vet_id: vetId, current_vet_name: vetName },
      }, '*');
    }
    set({
      currentVetId: vetId,
      currentVetName: vetName,
    });
  },

  clearCurrentVet: () => {
    localStorage.removeItem('current_vet_id');
    localStorage.removeItem('current_vet_name');
    set({
      currentVetId: null,
      currentVetName: null,
    });
  },

  setSubscription: (subscription) => {
    set({ subscription });
  },
}));
